// src/pages/manager/ManagerPageEditor.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { sectionTemplates } from "../../data/database";
import { pagesApi } from "../../api/pagesApi";

import SectionEditorModal from "../admin/components/SectionEditorModal";
import FooterEditorModal from "../admin/components/FooterEditorModal";
import NavEditorModal from "../admin/components/NavEditorModal";
import Toast from "../../components/GeneralComponents/Toast";

import { getManagerAuth } from "./managerPageEditor/utils/auth";
import { hydrateManagerState } from "./managerPageEditor/utils/hydrateManagerState";
import { openPageWithToast } from "./managerPageEditor/utils/openPageUtils";

import { useManagerLanguage } from "./managerPageEditor/hooks/useManagerLanguage";
import { useBlockAutosave } from "./managerPageEditor/hooks/useBlockAutosave";
import { useManagerSectionAutosave } from "./managerPageEditor/hooks/useManagerSectionAutosave";
import { useSectionOrderAutosave } from "./managerPageEditor/hooks/useSectionOrderAutosave";

import ManagerHeader from "./managerPageEditor/components/ManagerHeader";
import SectionsPanel from "./managerPageEditor/components/SectionsPanel";
import BlockCard from "./managerPageEditor/components/BlockCard";
import PreviewPanel from "./managerPageEditor/components/PreviewPanel";

export default function ManagerPageEditor() {
  const navigate = useNavigate();
  const params = useParams();
  const routeSlug = (params?.slug || "").trim();
  const tf = (key, fallback = "", vars = {}) => {
    let text = t(key, fallback);
    Object.entries(vars).forEach(([k, v]) => {
      text = text.replace(new RegExp(`{${k}}`, "g"), String(v));
    });
    return text;
  };
  const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  // toast
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });
  const showToast = ({ type = "success", title = "", message = "" }) => {
    setToast({ open: true, type, title, message });
  };

  // language (manager can change)
  const { lang, setLang, t, isRTL, ready } = useManagerLanguage({ apiBase: API_BASE, defaultLang: "ar" });
  // auth + pageId
  const [pageId, setPageId] = useState(null);

  // data
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [nav, setNav] = useState(null);
  const [footer, setFooter] = useState(null);
  const [whatsapp, setWhatsapp] = useState(null);

  // UI state
  const [saving, setSaving] = useState(false);

  const [showNavEditor, setShowNavEditor] = useState(false);
  const [showFooterEditor, setShowFooterEditor] = useState(false);

  const [editingSectionId, setEditingSectionId] = useState(null);
  const [showSectionEditor, setShowSectionEditor] = useState(false);

  const editingSection = useMemo(
    () => sections.find((s) => s.id === editingSectionId) || null,
    [sections, editingSectionId]
  );

  // keep latest sections for autosave safePatch lookups
  const sectionsRef = useRef([]);
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  const logout = () => {
    localStorage.removeItem("sb_token");
    localStorage.removeItem("sb_user");
    if (routeSlug) navigate(`/${routeSlug}`, { replace: true });
    else navigate("/", { replace: true });
  };

  // auth gate
  useEffect(() => {
    const { token, user, pageId: pid } = getManagerAuth();

    if (!token || !pid) {
      if (routeSlug) navigate(`/${routeSlug}`, { replace: true });
      else navigate("/", { replace: true });
      return;
    }

    if (user && !localStorage.getItem("sb_user")) {
      localStorage.setItem("sb_user", JSON.stringify(user));
    }

    setPageId(pid);
  }, [navigate, routeSlug]);

// ✅ acquire edit lock for manager editor (fast kick + no leaks)
useEffect(() => {
  if (!pageId) return;

  let stopped = false;
  let timer = null;

  // ✅ define these OUTSIDE try so cleanup can access them
  const loseLock = () => {
    if (stopped) return;
    stopped = true;

    if (timer) clearInterval(timer);

    const s = String(routeSlug || page?.slug || "").trim();
    navigate(s ? `/${s}` : "/", {
      replace: true,
      state: {
        dialog: {
          type: "error",
          title: t("manager.toast.lockLostTitle", "Editing ended"),
          message: t(
            "manager.toast.lockLostMsg",
            "You were removed because someone else started editing this page."
          ),
        },
      },
    });
  };

  const onVis = () => {
    // when tab becomes visible/focused, re-check lock immediately
    if (document.visibilityState === "visible") {
      pagesApi.heartbeatEditLock(pageId).catch(() => loseLock());
    }
  };

  async function acquire() {
    try {
      const r = await pagesApi.acquireEditLock(pageId);
      if (stopped) return;

      const serverEvery = Number(r?.heartbeatEveryMs) || 25000;

      // ✅ manager-side clamp: max 5s so takeover kicks quickly
      const every = Math.max(1000, Math.min(serverEvery, 2000));

     let failCount = 0;

    const shouldKickNow = (err) => {
      // pagesApi throws Error with err.data (from apiJson)
      const status = err?.status || err?.data?.status || err?.response?.status; // might be undefined
      const msg = String(err?.message || "");
      const apiError = String(err?.data?.error || "");

      // ✅ Only definitive "lost lock" is 409 from your server
      if (status === 409) return true;

      // Sometimes message text helps if status isn't attached
      if (msg.toLowerCase().includes("lock lost")) return true;
      if (apiError.toLowerCase().includes("lock lost")) return true;

      return false;
    };

    const heartbeatOnce = async () => {
      try {
        await pagesApi.heartbeatEditLock(pageId);
        failCount = 0; // ✅ reset on success
      } catch (e) {
        // ✅ if server says lock lost → kick immediately
        if (shouldKickNow(e)) {
          loseLock();
          return;
        }

        // ✅ otherwise transient error: retry a few times before kicking
        failCount += 1;

        // after 3 consecutive failures (~6s if every=2s), then kick
        if (failCount >= 3) {
          loseLock();
          return;
        }
      }
    };

    timer = setInterval(() => {
      if (stopped) return;
      heartbeatOnce();
    }, every);

      // ✅ attach listeners after we have the lock
      window.addEventListener("focus", onVis);
      document.addEventListener("visibilitychange", onVis);
    } catch (e) {
      const lock = e?.data?.lock;
      const roleRaw = String(lock?.role || "").toLowerCase();

      const actor =
        roleRaw === "admin"
          ? t("manager.lock.actorAdmin", "an admin")
          : roleRaw === "manager"
            ? t("manager.lock.actorManager", "a manager")
            : t("manager.lock.actorOther", "another user");

      const s = String(routeSlug || "").trim();
      navigate(s ? `/${s}` : "/", {
        replace: true,
        state: {
          dialog: {
            type: "error",
            title: t("manager.toast.lockedTitle", "Page is being edited"),
            message: tf(
              "manager.toast.lockedMsg",
              "You cannot enter because {actor} is editing this page right now.",
              { actor }
            ),
          },
        },
      });
    }
  }

  acquire();

  return () => {
    stopped = true;
    if (timer) clearInterval(timer);

    // IMPORTANT: remove listeners
    window.removeEventListener("focus", onVis);
    document.removeEventListener("visibilitychange", onVis);

    pagesApi.releaseEditLock(pageId).catch(() => {});
  };
}, [pageId, navigate, routeSlug, page, t]);

  // load page + keep slug route correct
  useEffect(() => {
    let ignore = false;

    async function load() {
      if (!pageId) return;

      try {
        const found = await pagesApi.getById(pageId);
        if (ignore) return;

        const realSlug = String(found?.slug || "").trim();
        if (realSlug && routeSlug && realSlug !== routeSlug) {
          navigate(`/${realSlug}/manager`, { replace: true });
        }

        setPage(found);

        const hydrated = hydrateManagerState(found, sectionTemplates);

        // apply saved language if exists
        if (hydrated.savedLang && ["en", "ar", "he"].includes(hydrated.savedLang)) {
          setLang(hydrated.savedLang);
        }

        setNav(hydrated.nav);
        setFooter(hydrated.footer);
        setSections(hydrated.sections);
        setWhatsapp(hydrated.whatsapp);
      } catch (e) {
        console.error("Failed to load page:", e);
        if (!ignore) setPage(null);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [pageId, navigate, routeSlug, setLang]);

  // autosave: nav/footer
  const { patchBlockLive: patchNavLive, flushBlockAutosave: flushNavAutosave } = useBlockAutosave({
    pageId,
    updateBlock: pagesApi.updateNav,
    setBlock: setNav,
    logLabel: "Nav",
  });

  const { patchBlockLive: patchFooterLive, flushBlockAutosave: flushFooterAutosave } = useBlockAutosave({
    pageId,
    updateBlock: pagesApi.updateFooter,
    setBlock: setFooter,
    logLabel: "Footer",
  });

  // autosave: sections (per section)
  const { patchSectionLive, flushSection, flushAllPendingSections } = useManagerSectionAutosave({
    pageId,
    updateSection: pagesApi.updateSection,
    setSections,
    sectionsRef,
  });

  // reorder autosave
  const { moveSection, flushOrderNow } = useSectionOrderAutosave({
    pageId,
    setSections,
    updatePage: pagesApi.update,
    onOrderSaveError: () => {
      showToast({
        type: "error",
        title: t("manager.toast.orderFailedTitle", "Order not saved"),
        message: t(
          "manager.toast.orderFailedMsg",
          "Failed to save section order. Please press Save to retry."
        ),
      });
    },
  });

  const editSection = (section) => {
    setEditingSectionId(section.id);
    setShowSectionEditor(true);
  };

  const openPage = () => {
    openPageWithToast({ page, routeSlug, t, showToast });
  };

  // manager save flushes everything pending
  const flushAll = async () => {
    setSaving(true);
    try {
      await flushAllPendingSections();
      await flushNavAutosave();
      await flushFooterAutosave();
      await flushOrderNow();

      showToast({
        type: "success",
        title: t("manager.toast.savedTitle", "Saved"),
        message: t("manager.toast.savedMsg", "Your changes were saved successfully."),
      });
    } catch (e) {
      showToast({
        type: "error",
        title: t("manager.toast.saveFailedTitle", "Save failed"),
        message: e?.message || t("manager.toast.saveFailedMsg", "Failed to save changes."),
      });
    } finally {
      setSaving(false);
    }
  };

  // UI guard
  if (!page) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm border p-6 text-center max-w-md w-full">
          <div className="text-xl font-bold text-gray-900">Page not found</div>
          <div className="text-gray-600 mt-2">This manager account has no valid page.</div>
          <div className="mt-4 flex gap-2 justify-center">
            <button onClick={logout} className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black">
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  const previewSlug = String(page?.slug || routeSlug || "").trim() || routeSlug;
if (!ready) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-sm border p-6 text-center max-w-md w-full">
        <div className="text-xl font-bold text-gray-900">Loading…</div>
        <div className="text-gray-600 mt-2">Preparing language</div>
      </div>
    </div>
  );
}
  return (
    <div className="min-h-screen bg-gray-50">
      <ManagerHeader
        t={t}
        isRTL={isRTL}
        pageName={page?.name || "Untitled"}
        previewSlug={previewSlug}
        lang={lang}
        setLang={setLang}
        onBack={() => navigate(`/${previewSlug}`)}
        onOpen={openPage}
        onSave={flushAll}
        saving={saving}
        onLogout={logout}
      />

      <div className="max-w-7xl mx-auto px-4 py-6 grid lg:grid-cols-4 gap-6">
        {/* left */}
        <div className="lg:col-span-1 space-y-6">
          <SectionsPanel
            t={t}
            sections={sections}
            sectionTemplates={sectionTemplates}
            onMove={moveSection}
            onEdit={editSection}
          />

          <BlockCard
            title={t("manager.navigation.title", "Navigation")}
            buttonText={t("manager.navigation.edit", "Edit Navigation")}
            onClick={() => setShowNavEditor(true)}
            disabled={!nav}
            description={t(
              "manager.navigation.description",
              "Navigation renders once at the top of the page (not inside the sections list)."
            )}
          />

          <BlockCard
            title={t("manager.footer.title", "Footer")}
            buttonText={t("manager.footer.edit", "Edit Footer")}
            onClick={() => setShowFooterEditor(true)}
            disabled={!footer}
            description={t(
              "manager.footer.description",
              "Footer renders once at the bottom of the page (not inside the sections list)."
            )}
          />
        </div>

        {/* right */}
        <div className="lg:col-span-3">
          <PreviewPanel
            t={t}
            sections={sections}
            footer={footer}
            nav={nav}
            whatsapp={whatsapp}
            previewSlug={previewSlug}
            onSectionClick={(section) => editSection(section)}
          />
        </div>
      </div>

      {/* modals */}
      {showFooterEditor && footer && (
        <FooterEditorModal
          footer={footer}
          onPatch={patchFooterLive}
          onClose={async () => {
            await flushFooterAutosave();
            setShowFooterEditor(false);
          }}
          uploadImage={pagesApi.uploadImage}
          t={t}
        />
      )}

      {showSectionEditor && editingSection && (
        <SectionEditorModal
          section={editingSection}
          onPatch={(patch) => patchSectionLive(editingSection.id, patch)}
          onSave={async () => {
            await flushSection(editingSection.id);
            setShowSectionEditor(false);
            setEditingSectionId(null);
          }}
          onClose={async () => {
            await flushSection(editingSection.id);
            setShowSectionEditor(false);
            setEditingSectionId(null);
          }}
        />
      )}

      {showNavEditor && nav && (
        <NavEditorModal
          nav={nav}
          onPatch={patchNavLive}
          onClose={async () => {
            await flushNavAutosave();
            setShowNavEditor(false);
          }}
          uploadImage={pagesApi.uploadImage}
          t={t}
        />
      )}

      <Toast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
}